import { useEffect, useMemo, useRef, useState } from 'react';
import { usePreviewExchangeNewItemsMutation } from '../../services/exchangeApi';

const getExchangeUnitPrice = (item) => {
  const quantity = parseFloat(item?.quantity || 0);
  const lineTotal = parseFloat(item?.line_total || 0);

  if (quantity > 0 && Number.isFinite(lineTotal)) {
    return lineTotal / quantity;
  }

  return parseFloat(item?.unit_price || 0);
};

export default function useDashboardExchange({
  billingMode,
  exchangeSourceOrder,
  exchangeReturnItems,
  exchangeNewItems,
  exchangeSalesperson,
  customerRequired,
  salespersonRequired,
  orgSettings,
  reasonCodes,
}) {
  const inExchangeWorkflow = billingMode === 'exchange' && !!exchangeSourceOrder;

  const exchangeReturnTotal = useMemo(() => {
    if (!exchangeSourceOrder) return 0;
    return (exchangeSourceOrder.items || []).reduce((sum, item) => {
      const sel = exchangeReturnItems[item.id];
      if (!sel?.selected) return sum;
      return sum + getExchangeUnitPrice(item) * sel.quantity;
    }, 0);
  }, [exchangeSourceOrder, exchangeReturnItems]);

  const rawExchangeNewTotal = useMemo(
    () => exchangeNewItems.reduce((sum, item) => sum + item.retail_price * item.quantity - (item.discount_amount || 0), 0),
    [exchangeNewItems]
  );

  const exchangeSalespersonId = exchangeSalesperson?.id || null;

  const exchangeNewItemsPayload = useMemo(
    () => exchangeNewItems.map((item) => ({
      product_id: item.product_id,
      quantity: item.quantity,
      ...(item.discount_id ? { discount_id: item.discount_id } : {}),
      ...(item.modified_value != null ? { modified_value: item.modified_value } : {}),
      ...(exchangeSalespersonId ? { salesperson_id: exchangeSalespersonId } : {}),
    })),
    [exchangeNewItems, exchangeSalespersonId]
  );

  // ── Live promotion preview ──────────────────────────────────────────────────
  //
  // exchangeNewItemsPayload is rebuilt (with the same values) on every render,
  // so a plain useEffect dependency on it would refire constantly — key off its
  // serialized contents instead, same trick a search-debounce would use on a
  // string. The preview call is skipped entirely while there are no new items
  // (nothing to promote, and the backend requires at least one) or no source
  // order yet (no org/customer context for the scratch order).
  const newItemsKey = JSON.stringify(exchangeNewItemsPayload);
  const [previewExchangeNewItems] = usePreviewExchangeNewItemsMutation();
  const [promotionPreview, setPromotionPreview] = useState(null);
  const [isPreviewingPromotions, setIsPreviewingPromotions] = useState(false);
  const previewDebounceRef = useRef(null);
  const previewRequestIdRef = useRef(0);

  useEffect(() => {
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);

    if (!inExchangeWorkflow || exchangeNewItemsPayload.length === 0) {
      setPromotionPreview(null);
      setIsPreviewingPromotions(false);
      return;
    }

    const requestId = ++previewRequestIdRef.current;
    setIsPreviewingPromotions(true);

    previewDebounceRef.current = setTimeout(async () => {
      try {
        const res = await previewExchangeNewItems({
          orderId: exchangeSourceOrder.id,
          new_items: exchangeNewItemsPayload,
          salesperson_id: exchangeSalespersonId,
        }).unwrap();
        // A later change may have already fired its own request while this one
        // was in flight — drop a stale response so it can't clobber a newer one.
        if (requestId !== previewRequestIdRef.current) return;
        setPromotionPreview(res?.data || null);
      } catch {
        if (requestId !== previewRequestIdRef.current) return;
        // Preview is a nice-to-have, not the source of truth — fall back to
        // the raw client-side total (below) rather than surface an error.
        setPromotionPreview(null);
      } finally {
        if (requestId === previewRequestIdRef.current) setIsPreviewingPromotions(false);
      }
    }, 350);

    return () => clearTimeout(previewDebounceRef.current);
  }, [newItemsKey, inExchangeWorkflow, exchangeSourceOrder?.id, exchangeNewItemsPayload, exchangeSalespersonId, previewExchangeNewItems]);

  // Promotion-corrected total once the preview lands; the raw per-item sum
  // until then (or if the preview failed) — same "optimistic, then
  // server-corrected" pattern the normal billing cart already uses.
  //
  // A preview with no subtotal while items exist is a failed one (older
  // backends answered failures with an all-zero 200) — never trust it, or
  // New Items shows ₹0 and the whole return credit is refunded.
  const previewUsable = promotionPreview && parseFloat(promotionPreview.subtotal) > 0;
  const exchangeNewTotal = previewUsable ? parseFloat(promotionPreview.grand_total) : rawExchangeNewTotal;
  const nearMissHints = promotionPreview?.near_miss_hints || [];
  const appliedPromotions = promotionPreview?.applied_promotions || [];

  const exchangeNetAmount = exchangeNewTotal - exchangeReturnTotal;
  const exchangeDirection =
    exchangeNetAmount > 0.005 ? 'customer_pays' :
    exchangeNetAmount < -0.005 ? 'customer_refund' : 'even';

  const exchangeReturnItemsPayload = useMemo(() => {
    if (!exchangeSourceOrder) return [];
    return (exchangeSourceOrder.items || [])
      .filter((item) => exchangeReturnItems[item.id]?.selected)
      .map((item) => {
        const sel = exchangeReturnItems[item.id];
        return {
          order_item_id: item.id,
          quantity_returned: sel.quantity,
          ...(sel.reason_code_id ? { return_reason_code_id: sel.reason_code_id } : {}),
        };
      });
  }, [exchangeSourceOrder, exchangeReturnItems]);

  const allReturnItemsHaveReasonCode =
    !(orgSettings.return_reason_required && reasonCodes.length > 0) ||
    exchangeReturnItemsPayload.every((item) => item.return_reason_code_id);

  // The exchange's customer is always the original bill's (locked); the
  // salesperson is picked on the exchange itself.
  const exchangeMissingCustomer = !!customerRequired && !exchangeSourceOrder?.customer_id;
  const exchangeMissingSalesperson = !!salespersonRequired && !exchangeSalespersonId;

  const canCompleteExchange =
    exchangeReturnItemsPayload.length > 0 &&
    exchangeNewItems.length > 0 &&
    allReturnItemsHaveReasonCode &&
    !exchangeMissingCustomer &&
    !exchangeMissingSalesperson;

  const exchangeBlockReason =
    exchangeMissingCustomer ? 'Customer required — the original bill has no customer'
    : !allReturnItemsHaveReasonCode ? 'Select a reason code for all return items'
    : exchangeMissingSalesperson ? 'Salesperson required — press F6 to add'
    : !canCompleteExchange ? 'Select return items and add new items'
    : '';

  return {
    inExchangeWorkflow,
    exchangeReturnTotal,
    exchangeNewTotal,
    exchangeNetAmount,
    exchangeDirection,
    exchangeReturnItemsPayload,
    exchangeNewItemsPayload,
    allReturnItemsHaveReasonCode,
    canCompleteExchange,
    exchangeBlockReason,
    nearMissHints,
    appliedPromotions,
    isPreviewingPromotions,
  };
}
